import React from 'react'
import { motion } from 'framer-motion'
import { useInView } from 'react-intersection-observer'
import { Settings, Users, Wrench, Clock, Award, Phone } from 'lucide-react'

const Services = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  })

  const services = [
    {
      icon: Users,
      title: "Consultation & Design",
      description: "Expert consultation to design the perfect Pigmentium™ lighting solution for your specific needs.",
      details: [
        "Site assessment and lighting analysis",
        "Custom system design and planning",
        "Energy efficiency calculations",
        "Cost-benefit analysis"
      ],
      duration: "1-2 weeks",
      price: "Free initial consultation"
    },
    {
      icon: Settings,
      title: "Installation & Setup",
      description: "Professional installation of your Pigmentium™ lighting systems by certified technicians.",
      details: [
        "Professional installation team",
        "Quality assurance testing",
        "System commissioning",
        "User training and handover"
      ],
      duration: "1-3 days",
      price: "Included in package"
    },
    {
      icon: Wrench,
      title: "Maintenance & Support",
      description: "Comprehensive maintenance and support services to ensure optimal performance.",
      details: [
        "Regular system health checks",
        "Preventive maintenance",
        "24/7 technical support",
        "Performance optimization"
      ],
      duration: "Ongoing",
      price: "Annual service plan"
    }
  ]

  const additionalServices = [
    {
      icon: Award,
      title: "Certification & Compliance",
      description: "Ensure your installation meets all safety and regulatory standards."
    },
    {
      icon: Clock,
      title: "Emergency Response",
      description: "24/7 emergency response for critical lighting system failures."
    },
    {
      icon: Phone,
      title: "Remote Monitoring",
      description: "Advanced IoT monitoring for real-time system performance tracking."
    }
  ]

  return (
    <section id="services" className="py-20 bg-white">
      <div className="container mx-auto px-6">
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 50 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-bold text-gray-800 mb-6">
            Our <span className="text-blue-500">Services</span>
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            From initial consultation to ongoing support, we provide comprehensive services 
            to ensure your Pigmentium™ lighting system delivers optimal performance.
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-3 gap-8 mb-16">
          {services.map((service, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 50 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.8, delay: index * 0.2 }}
              className="bg-gradient-to-br from-gray-50 to-blue-50 rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300"
            >
              <div className="flex items-center mb-6">
                <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-purple-600 rounded-xl flex items-center justify-center mr-4">
                  <service.icon className="w-8 h-8 text-white" />
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-gray-800">{service.title}</h3>
                  <div className="flex items-center mt-2 space-x-4">
                    <span className="text-sm text-blue-600 bg-blue-100 px-3 py-1 rounded-full">
                      {service.duration}
                    </span>
                    <span className="text-sm text-green-600 bg-green-100 px-3 py-1 rounded-full">
                      {service.price}
                    </span>
                  </div>
                </div>
              </div>
              
              <p className="text-gray-600 mb-6 leading-relaxed">{service.description}</p>
              
              <div className="space-y-3">
                <h4 className="text-lg font-semibold text-gray-800">What's Included:</h4>
                {service.details.map((detail, idx) => (
                  <div key={idx} className="flex items-center text-gray-600">
                    <div className="w-2 h-2 bg-blue-400 rounded-full mr-3 flex-shrink-0"></div>
                    <span>{detail}</span>
                  </div>
                ))}
              </div>
              
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="w-full mt-6 bg-gradient-to-r from-blue-500 to-purple-600 text-white font-semibold py-3 px-6 rounded-xl hover:shadow-lg transition-all duration-300"
              >
                Get Service Quote
              </motion.button>
            </motion.div>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.6 }}
          className="mb-16"
        >
          <h3 className="text-3xl font-bold text-center text-gray-800 mb-12">Additional Services</h3>
          <div className="grid md:grid-cols-3 gap-8">
            {additionalServices.map((service, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={inView ? { opacity: 1, scale: 1 } : {}}
                transition={{ duration: 0.6, delay: 0.8 + index * 0.1 }}
                className="text-center p-6 rounded-2xl bg-gradient-to-br from-yellow-50 to-orange-50 hover:shadow-lg transition-all duration-300"
              >
                <div className="w-16 h-16 bg-gradient-to-br from-yellow-400 to-orange-500 rounded-xl flex items-center justify-center mx-auto mb-4">
                  <service.icon className="w-8 h-8 text-white" />
                </div>
                <h4 className="text-xl font-bold text-gray-800 mb-3">{service.title}</h4>
                <p className="text-gray-600">{service.description}</p>
              </motion.div>
            ))}
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 1 }}
          className="bg-gradient-to-r from-blue-600 to-purple-700 rounded-2xl p-8 text-white text-center"
        >
          <h3 className="text-3xl font-bold mb-4">Need a Custom Service Package?</h3>
          <p className="text-xl mb-6 opacity-90">
            Every project is unique. Contact us to discuss your specific requirements 
            and get a tailored service package that fits your needs perfectly.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="bg-white text-blue-600 font-semibold py-3 px-8 rounded-xl hover:shadow-lg transition-all duration-300"
            >
              Schedule Consultation
            </motion.button>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="border-2 border-white text-white font-semibold py-3 px-8 rounded-xl hover:bg-white hover:text-blue-600 transition-all duration-300"
            >
              Download Service Brochure
            </motion.button>
          </div>
        </motion.div>
      </div>
    </section>
  )
}

export default Services 